import {
  FileText,
  ListChecks,
  Clock,
  AlertTriangle,
  Users,
  TrendingUp,
  Gavel,
  MapPin,
} from "lucide-react";
import { normalizeUrgency, formatUrgency } from "@/lib/scores";

function ScoreBar({ value, max = 10, color = "#C5A059" }) {
  const pct = Math.max(0, Math.min(100, ((value || 0) / max) * 100));
  return (
    <div className="w-full h-2 rounded-full bg-slate-200 overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

function ListBlock({ items, fallback = "Not provided", testId }) {
  const arr = Array.isArray(items) ? items.filter(Boolean) : [];
  if (arr.length === 0)
    return (
      <p data-testid={testId} className="text-sm text-slate-400 italic">
        {fallback}
      </p>
    );
  return (
    <ul data-testid={testId} className="space-y-2">
      {arr.map((x, i) => (
        <li
          key={`${i}-${typeof x === "string" ? x.slice(0, 40) : i}`}
          className="text-sm text-slate-700 leading-relaxed flex gap-2"
        >
          <span className="w-1 h-1 rounded-full bg-[#C5A059] mt-2 shrink-0" />
          <span>{x}</span>
        </li>
      ))}
    </ul>
  );
}

export default function CaseSummary({ summary, documents = [] }) {
  if (!summary) return null;
  // Defensive normalization: old DB rows may still hold `urgency_score` on a
  // 0–100 scale (legacy mirror bug). Clamp + scale via the shared helper so
  // attorneys never see "68/10" again.
  const urgency = normalizeUrgency(summary.urgency_score);
  const urgencyDisplay = formatUrgency(summary.urgency_score);
  const completeness = Math.max(0, Math.min(100, Number(summary.completeness_score ?? 0)));

  return (
    <div data-testid="case-summary" className="space-y-6">
      {/* Header */}
      <div className="bg-[#0A192F] text-white rounded-2xl p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
        <div className="relative">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059]">
            Attorney-Ready Case File
          </p>
          <h1 className="mt-3 font-heading text-3xl lg:text-4xl font-semibold leading-tight">
            {summary.case_type || "Legal Matter"}
          </h1>
          <div className="mt-4 flex flex-wrap gap-3 text-sm">
            {summary.attorney_specialty && (
              <span
                data-testid="summary-specialty"
                className="px-3 py-1 rounded-full bg-white/10 border border-white/20"
              >
                <Gavel className="inline w-3.5 h-3.5 mr-1.5" />
                {summary.attorney_specialty}
              </span>
            )}
            {(summary.state || summary.city) && (
              <span
                data-testid="summary-location"
                className="px-3 py-1 rounded-full bg-white/10 border border-white/20"
              >
                <MapPin className="inline w-3.5 h-3.5 mr-1.5" />
                {[summary.city, summary.state].filter(Boolean).join(", ")}
              </span>
            )}
            {summary.case_value_score && (
              <span
                data-testid="summary-value-score"
                className={`px-3 py-1 rounded-full font-semibold ${
                  summary.case_value_score === "high"
                    ? "bg-[#C5A059] text-[#0A192F]"
                    : summary.case_value_score === "medium"
                      ? "bg-amber-200 text-amber-900"
                      : "bg-white/10 border border-white/20"
                }`}
              >
                {summary.case_value_score.toUpperCase()} value
              </span>
            )}
            {summary.estimated_value_band && (
              <span
                data-testid="summary-value-band"
                className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-200"
              >
                Est. {summary.estimated_value_band}
              </span>
            )}
            {summary.urgency_level && (
              <span
                data-testid="summary-urgency-level"
                className={`px-3 py-1 rounded-full font-semibold ${
                  summary.urgency_level === "high"
                    ? "bg-red-500/20 text-red-200 border border-red-400/30"
                    : "bg-white/10 border border-white/20"
                }`}
              >
                {summary.urgency_level.toUpperCase()} urgency
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Scores */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
              Urgency
            </p>
            <span
              data-testid="urgency-score"
              className="font-heading text-2xl font-bold text-[#0A192F]"
            >
              {urgencyDisplay}/10
            </span>
          </div>
          <ScoreBar
            value={urgency}
            max={10}
            color={urgency >= 7 ? "#dc2626" : "#C5A059"}
          />
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
              Completeness
            </p>
            <span
              data-testid="completeness-score"
              className="font-heading text-2xl font-bold text-[#0A192F]"
            >
              {completeness}/100
            </span>
          </div>
          <ScoreBar value={completeness} max={100} color="#0A192F" />
        </div>
      </div>

      {/* Bento grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Executive Summary - wide */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl p-7">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-4 h-4 text-[#C5A059]" />
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
              Executive Summary
            </p>
          </div>
          <p
            data-testid="executive-summary"
            className="text-slate-700 leading-relaxed"
          >
            {summary.executive_summary || "No summary provided."}
          </p>
        </div>

        {/* Timeline */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-7">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-[#C5A059]" />
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
              Timeline
            </p>
          </div>
          <ListBlock items={summary.timeline} testId="summary-timeline" />
        </div>

        {/* Key Facts */}
        <div className="bg-white border border-slate-200 rounded-xl p-7">
          <div className="flex items-center gap-2 mb-4">
            <ListChecks className="w-4 h-4 text-[#C5A059]" />
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
              Key Facts
            </p>
          </div>
          <ListBlock items={summary.key_facts} testId="summary-key-facts" />
        </div>

        {/* Damages */}
        <div className="bg-white border border-slate-200 rounded-xl p-7">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[#C5A059]" />
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
              Damages
            </p>
          </div>
          <ListBlock items={summary.damages} testId="summary-damages" />
        </div>

        {/* Parties */}
        <div className="bg-white border border-slate-200 rounded-xl p-7">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-[#C5A059]" />
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
              Parties Involved
            </p>
          </div>
          <ListBlock items={summary.parties_involved} testId="summary-parties" />
        </div>

        {/* Injuries / Financial */}
        <div className="bg-white border border-slate-200 rounded-xl p-7">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-[#C5A059]" />
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
              Injuries & Losses
            </p>
          </div>
          <div className="space-y-3">
            <ListBlock items={summary.injuries} testId="summary-injuries" />
            <ListBlock
              items={summary.financial_losses}
              testId="summary-financial"
            />
          </div>
        </div>

        {/* Evidence */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-7">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-4 h-4 text-[#C5A059]" />
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
              Supporting Evidence
            </p>
          </div>
          <ListBlock
            items={summary.supporting_evidence}
            testId="summary-evidence"
          />
          {documents.length > 0 && (
            <div className="mt-5 pt-5 border-t border-slate-100">
              <p className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500 mb-3">
                Uploaded Documents ({documents.length})
              </p>
              <ul className="space-y-2">
                {documents.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center gap-2 text-sm text-slate-700"
                  >
                    <FileText className="w-4 h-4 text-[#C5A059]" />
                    {d.original_filename}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Missing info */}
        <div className="lg:col-span-3 bg-[#FFF8E7] border border-[#C5A059]/40 rounded-xl p-7">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-[#C5A059]" />
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-700">
              Missing Information (helpful for attorneys)
            </p>
          </div>
          <ListBlock
            items={summary.missing_information}
            fallback="Nothing flagged."
            testId="summary-missing"
          />
        </div>
      </div>

      <p
        data-testid="summary-disclaimer"
        className="text-xs text-slate-500 leading-relaxed text-center max-w-3xl mx-auto"
      >
        This summary was generated by Lexi AI based on the conversation you
        provided. LexIntake is not a law firm and does not provide legal
        advice. No attorney-client relationship is formed.
      </p>
    </div>
  );
}
